import {Phone, Calendar, Shield, Check, Zap, MapPin, Car, Award} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const TeslaModelXPpfPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does Tesla Model X PPF cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Model X partial front starts at $1,299, full front starts at $1,899, and full vehicle PPF starts at $4,499. As a larger SUV with Falcon Wing door complexity, exact Model X pricing is often above these starting points and confirmed after inspection."
          }
        },
        {
          "@type": "Question",
          "name": "Can PPF protect Model X Falcon Wing doors?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. We apply PPF to Falcon Wing door edges, mechanisms, and painted surfaces. Protection prevents parking lot damage and maintains door operation without interference."
          }
        },
        {
          "@type": "Question",
          "name": "How long does Model X PPF take to install?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front takes 1-2 days, full front requires 2-3 days, and full vehicle PPF takes 5-7 days for Model X due to SUV size, Falcon Wing doors, and complex surfaces requiring expert handling."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Tesla Model X PPF San Diego | Luxury SUV Paint Protection"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium Tesla Model X PPF installation in San Diego. Protect your luxury SUV with professional paint protection film. Falcon Wing door protection, self-healing technology, manufacturer-backed film warranties.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/tesla-model-x-ppf-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Tesla Model X PPF San Diego - Luxury SUV Paint Protection Film</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your Tesla Model X with expert paint protection film installation in San Diego. Our installers specialize in luxury SUV PPF, delivering careful protection for Model X's unique Falcon Wing doors, expansive body panels, and premium finishes with precision craftsmanship.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              The Model X combines family practicality with Tesla's most advanced technology and distinctive design. Our Model X PPF installations protect this luxury SUV investment using premium self-healing film and SUV-specific techniques—ensuring comprehensive coverage across the large hood, Falcon Wing doors, rear liftgate, and all vulnerable surfaces. Serving families in Rancho Santa Fe, La Jolla, Del Mar, and throughout San Diego County.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Model X PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Model X Needs PPF */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Tesla Model X Owners Need Professional PPF</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              The Tesla Model X represents the pinnacle of electric luxury SUVs, combining seating for seven, distinctive Falcon Wing doors, and cutting-edge Autopilot technology. However, Model X shares Tesla's thin water-based paint that's highly susceptible to damage. <a href="/paint-protection-film" className="text-cyan-400 hover:underline">Paint protection film</a> provides essential armor for this premium family vehicle, protecting your investment through years of active use.
            </p>
            <p>
              Model X SUVs face unique challenges in San Diego: family road trips expose the vehicle to highway debris on I-5 and I-15, beach adventures bring salt spray and sand, Falcon Wing doors open in tight parking structures where adjacent vehicles cause door edge damage, and frequent cargo loading scratches rear bumpers and liftgate areas. PPF shields against all these threats while maintaining the sophisticated appearance expected from a $100,000+ luxury SUV.
            </p>
            <p>
              Our Model X PPF installations require advanced SUV expertise. The large hood, complex fender curves, unique Falcon Wing door edges, and expansive rear liftgate demand precision templates and expert handling. We protect critical areas including the aggressive front fascia, door edges (especially vulnerable on Falcon Wings), rocker panels, rear cargo area, and all high-contact surfaces families touch daily.
            </p>
            <p>
              Model X owners frequently combine <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF</a> with <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> for a blacked-out luxury appearance, plus <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> for cabin cooling and privacy. Package pricing makes complete Model X protection and customization affordable in one visit to our Vista facility serving all San Diego County families.
            </p>
          </div>
        </div>
      </section>

      {/* Model X Coverage Options & Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model X PPF Coverage & Pricing</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Essential Model X protection for high-impact areas: front bumper, partial hood (18-24"), mirrors, and headlights. Shields your luxury SUV's most vulnerable zones from highway debris.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complete front bumper coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (18-24 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Side mirrors and headlights</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Premium self-healing film</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">RECOMMENDED</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Comprehensive Model X front-end shield: full hood, bumper, fenders, mirrors, lights, and front door protection. Maximum highway and family adventure protection.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All partial coverage included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Falcon Wing door edge protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Protection</h3>
              <p className="text-gray-300 mb-6">
                Complete Model X protection covering every painted surface including Falcon Wing doors, liftgate, and roof. Ultimate family SUV protection and investment preservation.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front areas covered</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Falcon Wing doors and rockers</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, liftgate, rear bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Permanent luxury SUV protection</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            All packages include professional paint preparation, precision installation, and manufacturer warranty. Starting prices shown are for Tesla Model X. Exact pricing determined after consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Model X Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model X-Specific PPF Protection Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Falcon Wing Door Protection</h3>
              <p className="text-gray-300">Unique Falcon Wing doors require specialized PPF to protect edges, mechanisms, and painted surfaces from parking structure contact and weather exposure.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Liftgate & Cargo Shield</h3>
              <p className="text-gray-300">Family loading, groceries, sports equipment, and luggage constantly contact Model X's rear areas. PPF prevents scratches and maintains pristine cargo zones.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Surface</h3>
              <p className="text-gray-300">Minor scratches from car seats, strollers, and daily family use disappear with heat, keeping your Model X looking showroom-new through years of active lifestyle.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Luxury SUV Resale Value</h3>
              <p className="text-gray-300">Protected Model X commands premium resale prices. Full vehicle PPF maintains like-new condition, justifying higher asking prices and attracting serious buyers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model X PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Luxury SUV Consultation</h3>
                <p className="text-gray-300">We conduct a thorough Model X inspection, evaluating paint condition and Falcon Wing door mechanisms. We discuss your family's usage patterns—daily driving, road trips, off-road adventures—and recommend coverage options that match your luxury SUV protection needs and budget. You receive a transparent quote with premium film options.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Premium Paint Preparation</h3>
                <p className="text-gray-300">Your Model X receives luxury-grade preparation: hand washing with pH-balanced automotive shampoo, complete clay bar decontamination, iron remover treatment, and multi-stage paint correction if needed. We remove all swirl marks, water spots, and imperfections from the large hood, doors, and body panels—creating a flawless canvas for PPF application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Expert SUV PPF Application</h3>
                <p className="text-gray-300">Using Model X-specific bulk or precision-pattern methods, we apply premium self-healing PPF with advanced wet installation techniques designed for large SUV surfaces. Falcon Wing doors receive special attention—we protect door edges and mechanisms without interfering with operation. All trim pieces, sensors, and badges are properly disassembled and reinstalled for seamless edge-to-edge coverage.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Assurance & Delivery</h3>
                <p className="text-gray-300">Rigorous multi-point inspection ensures flawless edges, zero bubbles, complete adhesion, and proper Falcon Wing door operation. We verify all sensors, cameras, and Autopilot systems function correctly, test door mechanisms, provide maintenance instructions, deliver warranty documentation, and return your Model X in pristine protected condition.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model X Protection Zones */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Critical Model X Protection Zones</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Falcon Wing Door Edges</h3>
              <p className="text-gray-300 mb-4">Model X's signature Falcon Wing doors open upward in tight spaces, making door edges prone to contact with parking structure ceilings, adjacent vehicles, and garage door frames. Without protection, these painted edges chip and scratch quickly.</p>
              <p className="text-gray-300">We apply precision PPF to all Falcon Wing door edges and mechanisms, creating invisible protection that prevents damage while maintaining smooth door operation and distinctive appearance.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Expansive Hood & Fenders</h3>
              <p className="text-gray-300 mb-4">Model X features Tesla's largest hood and prominent fender flares. These expansive surfaces face constant rock chips, bugs, and road debris during San Diego freeway driving and family road trips to beaches and mountains.</p>
              <p className="text-gray-300">Full hood and fender PPF provides comprehensive front-end protection across large surfaces, preventing the dozens of chips that would otherwise accumulate during highway commutes and weekend adventures.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Rear Liftgate & Cargo Area</h3>
              <p className="text-gray-300 mb-4">Active families load groceries, strollers, sports equipment, camping gear, and luggage into Model X cargo areas multiple times daily. Rear liftgate sills and bumpers develop scratches from constant contact within months of ownership.</p>
              <p className="text-gray-300">Rear PPF creates a scratch-proof barrier on the liftgate edge, bumper, and cargo loading zones—maintaining pristine appearance despite heavy family use and frequent loading cycles.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Rocker Panels & Lower Surfaces</h3>
              <p className="text-gray-300 mb-4">Model X's SUV height still exposes rockers and lower panels to road spray, gravel, tar, and salt from coastal driving. These surfaces show damage first and affect overall appearance from side profile views.</p>
              <p className="text-gray-300">Rocker panel PPF shields these vulnerable areas from constant road debris assault, maintaining clean body lines and preventing unsightly chipping along the Model X's entire profile.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model X PPF FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does Tesla Model X PPF cost in San Diego?</h3>
              <p className="text-gray-300">Model X partial front starts at $1,299, full front starts at $1,899, and full vehicle PPF starts at $4,499. As a larger SUV, Model X's larger surface area, complex Falcon Wing doors, and premium status typically place final pricing above these starting points. Final cost depends on paint condition, coverage areas, and whether you have 5-seat, 6-seat, or 7-seat configuration. We provide complete quotes with itemized pricing.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF protect Model X Falcon Wing doors?</h3>
              <p className="text-gray-300">Yes. We apply PPF to Falcon Wing door edges, painted surfaces, and vulnerable areas without interfering with door mechanisms or sensors. Protection prevents parking structure contact damage and maintains smooth operation. Many Rancho Santa Fe and La Jolla Model X owners prioritize Falcon Wing door edge protection due to tight parking spaces.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does Model X PPF take to install?</h3>
              <p className="text-gray-300">Partial front takes 1-2 days, full front requires 2-3 days, and full vehicle PPF takes 5-7 days for Model X. Installation time accounts for larger SUV surface area, complex Falcon Wing door disassembly, and meticulous quality standards required for luxury vehicles. We never rush precision work—proper application ensures 10+ years of flawless protection.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF worth it for a $100,000+ Model X Plaid?</h3>
              <p className="text-gray-300">Absolutely. Model X Plaid represents a significant luxury investment. PPF prevents paint damage that reduces resale value by thousands of dollars and maintains showroom appearance throughout ownership. Most Plaid owners choose full vehicle coverage to protect their entire investment and ensure maximum resale premium when upgrading.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF be applied to a used Model X?</h3>
              <p className="text-gray-300">Yes. We perform comprehensive paint correction to remove existing damage before PPF installation, restoring like-new appearance. Used Model X vehicles often show swirl marks from automatic car washes and scratches from family use—we eliminate these issues, then apply PPF to lock in the corrected finish and prevent future damage.</p>
            </div>
            <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I combine Model X PPF with other services?</h3>
              <p className="text-gray-300">Yes! Many families combine <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF</a>, <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a>, <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a>, and <a href="/caliper-painting" className="text-cyan-400 hover:underline">caliper painting</a> for complete Model X protection and customization. Package discounts available for multi-service installations completed in one visit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Tesla Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/tesla-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Professional PPF for all Tesla models including Model S, 3, Y, and Cybertruck</p>
            </a>
            <a href="/paint-protection-film-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Premium PPF installation for all luxury SUVs and performance vehicles</p>
            </a>
            <a href="/chrome-delete-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete San Diego</h3>
              <p className="text-gray-400 text-sm">Blacked-out trim and window surrounds for sleek Model X customization</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Tesla Model X PPF Serving Luxury Families Across San Diego
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in Vista, we provide expert Model X paint protection film services to luxury SUV owners in Rancho Santa Fe, La Jolla, Del Mar, and throughout San Diego County.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-center">
            <div>
              <p className="font-semibold text-white mb-2">Premium Communities</p>
              <p className="text-sm"><a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a> • <a href="/ppf-la-jolla" className="text-cyan-400 hover:underline">La Jolla</a> • <a href="/ppf-del-mar" className="text-cyan-400 hover:underline">Del Mar</a> • <a href="/ppf-solana-beach" className="text-cyan-400 hover:underline">Solana Beach</a></p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">North County</p>
              <p className="text-sm"><a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> • <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a> • <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a> • <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a> • <a href="/ppf-san-marcos" className="text-cyan-400 hover:underline">San Marcos</a> • <a href="/ppf-poway" className="text-cyan-400 hover:underline">Poway</a></p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">Metro San Diego</p>
              <p className="text-sm"><a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a> • Carmel Valley • Scripps Ranch • <a href="/ppf-escondido" className="text-cyan-400 hover:underline">Escondido</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Tesla Model X Investment</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free Model X PPF consultation and receive a custom quote for your luxury SUV. Same-week appointments available for San Diego County families.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Model X PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default TeslaModelXPpfPage
